import { useState } from "react";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { MessageSquare, Swords, Send, Bug, ChevronLeft, Sparkles } from "lucide-react";
import type { ActionTemplate } from "@/lib/mechanics/action-templates";

interface QuickChoice {
  text: string;
}

export interface QuickActionPrompt {
  key: string;
  label: string;
  options: string[];
}

export interface StructuredAction {
  id: string;
  label: string;
  icon?: string;
  message_template: string;
  prompts: QuickActionPrompt[];
}

export function extractStructuredActions(metadata?: Record<string, any>): StructuredAction[] | null {
  if (!metadata) return null;
  const raw = metadata.quick_actions;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const valid: StructuredAction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    if (!item.id || !item.label || !item.message_template) continue;
    valid.push({
      id: String(item.id),
      label: String(item.label),
      icon: item.icon ? String(item.icon) : undefined,
      message_template: String(item.message_template),
      prompts: Array.isArray(item.prompts)
        ? item.prompts.filter((p: any) => p && p.key && p.label && Array.isArray(p.options))
        : [],
    });
  }
  return valid.length > 0 ? valid : null;
}

interface QuickActionBubbleProps {
  isMobile: boolean;
  canAct: boolean;
  isGenerating?: boolean;
  choices?: QuickChoice[];
  actionTemplates?: ActionTemplate[];
  onSelectChoice?: (text: string) => void;
  onSelectAction?: (templateId: string) => void;
  onSendCommand?: (text: string) => void;
  onDebug?: () => void;
  /** AI-generated structured actions with multi-level prompts. */
  structuredActions?: StructuredAction[] | null;
}

export function QuickActionBubble({
  isMobile,
  canAct,
  isGenerating = false,
  choices = [],
  actionTemplates = [],
  structuredActions,
  onSelectChoice,
  onSelectAction,
  onSendCommand,
  onDebug,
}: QuickActionBubbleProps) {
  const [open, setOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState("");

  // Structured action multi-level flow
  const [activeStructured, setActiveStructured] = useState<StructuredAction | null>(null);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

  const hasStructured = !!structuredActions?.length;

  const visibleChoices = choices.slice(0, 4);
  const visibleActions = actionTemplates.slice(0, 6);

  const handleChoice = (text: string) => {
    onSelectChoice?.(text);
    setOpen(false);
  };

  const handleAction = (templateId: string) => {
    onSelectAction?.(templateId);
    setOpen(false);
  };

  const handleCommand = (message: string) => {
    onSendCommand?.(message);
    setOpen(false);
  };

  const trigger = (
    <button
      type="button"
      disabled={!canAct}
      className={cn(
        "mx-auto flex min-h-[60px] w-full max-w-[340px] items-center justify-between rounded-full border px-5 py-3 text-left shadow-lg transition-all",
        "bg-background/25 backdrop-blur-xl border border-white/10 hover:border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.25)]",
        open && "border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.4)]",
        !canAct && "cursor-not-allowed opacity-50",
      )}
      aria-label="Open quick actions"
    >
      <div className="space-y-0.5">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground/60">Quick actions</div>
        <div className="text-base text-foreground/85">What do you do next?</div>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
        {isGenerating ? <span>Thinking…</span> : <span>Open</span>}
        <span className={cn("transition-transform", open && "rotate-180")}>⌃</span>
      </div>
    </button>
  );

  const hasChoices = visibleChoices.length > 0;
  const hasActions = visibleActions.length > 0;

  const handleCustomSend = () => {
    const text = customInput.trim();
    if (!text) return;
    onSendCommand?.(text);
    setCustomInput("");
    setOpen(false);
  };

  // Structured action flow
  const startStructured = (action: StructuredAction) => {
    setActiveStructured(action);
    setSelections({});
    setCurrentPromptIndex(0);
  };
  const selectOption = (value: string) => {
    if (!activeStructured) return;
    const prompt = activeStructured.prompts[currentPromptIndex];
    if (!prompt) return;
    const next = { ...selections, [prompt.key]: value };
    setSelections(next);
    if (currentPromptIndex + 1 < activeStructured.prompts.length) {
      setCurrentPromptIndex(currentPromptIndex + 1);
    } else {
      let msg = activeStructured.message_template;
      for (const [k, v] of Object.entries(next)) msg = msg.replace(`{${k}}`, v);
      onSendCommand?.(msg);
      setActiveStructured(null);
      setSelections({});
      setCurrentPromptIndex(0);
      setOpen(false);
    }
  };
  const cancelStructured = () => {
    setActiveStructured(null);
    setSelections({});
    setCurrentPromptIndex(0);
  };

  const panel = (
    <div className="p-2.5 space-y-1">
      {/* ── DEBUG (admin only) ── */}
      {onDebug && (
        <div>
          <button
            onClick={() => setExpandedSection(expandedSection === "debug" ? null : "debug")}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors border border-dashed border-amber-500/20"
          >
            <Bug className="w-4 h-4 text-amber-500" />
            <span>Debug</span>
            <span className="ml-auto text-xs text-muted-foreground/30">V2 Flow</span>
            <span className={cn("text-muted-foreground/30 transition-transform text-sm", expandedSection === "debug" && "rotate-180")}>▾</span>
          </button>
          {expandedSection === "debug" && (
            <div className="px-1 pb-2 space-y-1">
              <button
                onClick={() => { onDebug(); setOpen(false); setExpandedSection(null); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left hover:bg-white/5 transition-colors"
              >
                <Bug className="w-3.5 h-3.5 shrink-0 text-amber-500 mt-px" />
                <span>Open debug canvas</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── STRUCTURED FLOW ── */}
      {activeStructured ? (
        <div className="px-1 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={cancelStructured} className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/10 shrink-0">
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            </button>
            <span className="text-sm font-medium">{activeStructured.label}</span>
            <span className="text-[10px] text-muted-foreground/50 ml-auto tabular-nums">
              {currentPromptIndex + 1}/{activeStructured.prompts.length}
            </span>
          </div>
          <div className="text-xs text-muted-foreground/60 mb-2">
            {activeStructured.prompts[currentPromptIndex]?.label}
          </div>
          <div className="flex flex-wrap gap-1">
            {activeStructured.prompts[currentPromptIndex]?.options.map((opt, i) => (
              <button
                key={i}
                disabled={!canAct}
                onClick={() => selectOption(opt)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium",
                  "bg-muted/30 hover:bg-muted/60 border border-border/20 hover:border-primary/25 transition-colors shrink-0",
                  !canAct && "opacity-40 pointer-events-none",
                )}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* ── SUGGESTED (AI-structured) ── */}
          {hasStructured && (
            <div>
              <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50 mb-1.5">
                <Sparkles className="w-3 h-3" /> Suggested
              </div>
              <div className="flex flex-wrap gap-1">
                {structuredActions!.map(sa => (
                  <button
                    key={sa.id}
                    disabled={!canAct}
                    onClick={() => startStructured(sa)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium",
                      "bg-primary/10 hover:bg-primary/20 border border-primary/20 hover:border-primary/30 transition-colors shrink-0",
                      !canAct && "opacity-40 pointer-events-none",
                    )}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    {sa.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── REPLY ── */}
          {hasChoices && (
        <div>
          <button
            onClick={() => setExpandedSection(expandedSection === "reply" ? null : "reply")}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-muted/40 transition-colors"
          >
            <MessageSquare className="w-4 h-4 text-sky-500" />
            <span>Reply</span>
            <span className="ml-auto text-xs text-muted-foreground/40 tabular-nums">{visibleChoices.length}</span>
            <span className={cn("text-muted-foreground/30 transition-transform text-sm", expandedSection === "reply" && "rotate-180")}>▾</span>
          </button>
          {expandedSection === "reply" && (
            <div className="space-y-0.5 px-1 pb-2">
              {visibleChoices.map((choice, i) => (
                <button
                  key={`${choice.text}-${i}`}
                  disabled={!canAct}
                  onClick={() => handleChoice(choice.text)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left",
                    "hover:bg-muted/50 transition-colors",
                    !canAct && "opacity-40 pointer-events-none",
                  )}
                >
                  <MessageSquare className="w-3.5 h-3.5 shrink-0 text-muted-foreground mt-px" />
                  <span className="leading-snug">{choice.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ACTION ── */}
      {hasActions && (
        <div>
          <button
            onClick={() => setExpandedSection(expandedSection === "action" ? null : "action")}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-muted/40 transition-colors"
          >
            <Swords className="w-4 h-4 text-red-500" />
            <span>Action</span>
            <span className="ml-auto text-xs text-muted-foreground/40 tabular-nums">{visibleActions.length}</span>
            <span className={cn("text-muted-foreground/30 transition-transform text-sm", expandedSection === "action" && "rotate-180")}>▾</span>
          </button>
          {expandedSection === "action" && (
            <div className="flex flex-wrap gap-1.5 px-1 pb-2">
              {visibleActions.map(action => (
                <button
                  key={action.id}
                  disabled={!canAct}
                  onClick={() => handleAction(action.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium",
                    "bg-muted/30 hover:bg-muted/60 border border-border/20 hover:border-primary/25 transition-colors shrink-0",
                    !canAct && "opacity-40 pointer-events-none",
                  )}
                >
                  <Swords className="w-3.5 h-3.5 text-muted-foreground" />
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CUSTOM RESPONSE ── */}
      <div className="border-t border-white/10 mt-1 pt-2 px-1 pb-1">
        <textarea
          value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleCustomSend();
            }
          }}
          placeholder="Or type your own…"
          disabled={!canAct}
          rows={2}
          className="w-full resize-none bg-transparent text-sm placeholder:text-muted-foreground/40 outline-none"
        />
        <div className="flex items-center justify-end mt-1">
          <button
            onClick={handleCustomSend}
            disabled={!canAct || !customInput.trim()}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium",
              "bg-white/10 hover:bg-white/20 transition-colors",
              (!canAct || !customInput.trim()) && "opacity-30 pointer-events-none",
            )}
          >
            <Send className="w-3.5 h-3.5" />
            Send
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[82vh] rounded-t-[28px]">
          <DrawerTitle className="sr-only">Quick actions</DrawerTitle>
          <ScrollArea className="max-h-[72vh]">{panel}</ScrollArea>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side="top" align="center" sideOffset={10} className="w-[min(380px,calc(100vw-1rem))] rounded-2xl border border-white/10 p-0 shadow-[0_8px_32px_rgba(0,0,0,0.35)] bg-background/25 backdrop-blur-xl">
        {panel}
      </PopoverContent>
    </Popover>
  );
}